import styled, { css } from 'styled-components';
import {
  StyledComponentsPropWithTheme,
  ThemeStyles,
} from 'src/commons/components/ChromeApp/ChromeAppUtils';
import {
  Button,
  Divider,
  isMobileStyleMixin as isMobile,
  isNotMobileStyleMixin as isNotMobile,
  Link,
  SearchInput,
  Text,
  TextButton,
} from '@amzn/storm-ui';
import { IntroWidgetAnchor } from '@amzn/intro-widgets-api';
import { concrete, gray800, gray900, mercury, white } from 'src/colors';

export const accountSelectionListId = `top-bar-utilities-acc-selector`;
export const currentSelectionClassName = `top-bar-utilities-current-selection`;

interface AccountDropdownButtonProps extends StyledComponentsPropWithTheme {
  active?: boolean;
  variant?: 'inactive' | 'active';
}

export const containerWithBorderTopMixin = css`
  border-top: 1px solid #e7e7e7;
`;

export const containerWithBorderBottomMixin = css`
  border-bottom: 1px solid #e7e7e7;
`;

export const AccountDropdownButton = styled(Button)<AccountDropdownButtonProps>`
  width: auto;
  position: relative;
  margin-right: 5px;
  padding-right: 16px;
  padding-left: 8px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 3px;
  background: transparent;
  border-style: hidden;
  ${({ theme }: { theme: ThemeStyles }) => {
    const backgroundColor = theme.header.utility.icon.background;
    return backgroundColor
      ? css`
          background-color: ${backgroundColor};
        `
      : '';
  }};
  color: ${({ theme }) => theme.header.utility.icon.foreground};

  ${({ active, theme }) =>
    active &&
    `
  color: ${theme.header.utility.icon.foregroundActive};
  opacity: 1;
  background-color: ${theme.header.utility.icon.backgroundActive};
`}
  &:active {
    transform: scale(0.95);
    transition-duration: 0.1s;
  }

  &:hover {
    color: ${({ theme }) => theme.header.utility.icon.foregroundHover};
    opacity: 1;
    background: ${({ theme }) => theme.header.utility.icon.backgroundHover};
    cursor: pointer;
  }
`;

export const AccountDropdownLink = styled.div`
  font-size: 13px;
  padding: 0 7px 2px 7px;
  border: none;
  outline: none;
  background: transparent;

  &:after {
    border: solid ${({ theme }) => theme.header.utility.icon.foreground};
    border-width: 0 2px 2px 0;
    display: inline-block;
    padding: 3px;
    transform: rotate(${document.documentElement.dir === 'rtl' ? -45 : 45}deg);
    -webkit-transform: rotate(
      ${document.documentElement.dir === 'rtl' ? -45 : 45}deg
    );
    content: '';
    position: absolute;
    top: calc(50% - 6px);
    right: 8px;
  }
`;

export const IA2AccountDropdownButton = styled(
  Button,
)<AccountDropdownButtonProps>`
  margin-right: 5px;
  background-color: ${({ variant }) =>
    variant === 'inactive' ? 'rgba(0, 0, 0, 0.4)' : gray900};
  color: ${white};
  box-shadow: 0px 1px 0px rgba(0, 0, 0, 0.2);

  ${isNotMobile(css`
    height: 100%;
  `)}
  ${isMobile(css`
    width: 100%;
  `)}
  ${({ active }) =>
    active &&
    css`
      background: ${gray800};
    `}
  &:active {
    transform: scale(0.95);
    transition-duration: 0.1s;
  }

  ${({ variant }) => {
    if (variant === 'inactive') {
      return css`
        pointer-events: none;

        &:hover,
        &:focus {
          background-color: rgba(0, 0, 0, 0.5);
        }
      `;
    }

    return css`
      &:hover,
      &:focus {
        background: ${gray800};
        box-shadow: 0px 2px 1px rgba(0, 0, 0, 0.2);
      }
    `;
  }}
`;

export const AccountName = styled.div`
  text-align: right;
  line-height: 16px;
  color: ${({ theme }) => theme.header.utility.icon.foreground};
`;

export const CurrentMarketplaceInfo = styled.div`
  text-align: right;
  font-size: 11px;
  line-height: 16px;
`;

export const AccountDropdownWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${white};
  width: 350px;
  height: initial;
  border-radius: 6px;
  overflow: hidden;

  /* Edge */
  @supports (-ms-ime-align: auto) {
    & {
      overflow-x: hidden;
    }
  }
`;

interface SpinnerContainerProps {
  height?: string;
  minHeight: string;
}

export const SpinnerContainer = styled.div<SpinnerContainerProps>`
  ${(props) => (props.height !== undefined ? `height: ${props.height}` : '')}
  min-height: ${(props) => props.minHeight};
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.5;
`;

export const AccountDropdownContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-top: 18px;
`;

export const AccountDropdownLinksWrapper = styled.div<{
  withMagellanPadding?: boolean;
}>`
  ${({ withMagellanPadding }) =>
    !withMagellanPadding
      ? css`
          padding: ${({ theme }) => theme.spacing.base}
            ${({ theme }) => theme.spacing.medium};
        `
      : css`
          padding: ${({ theme }) => theme.spacing.base};
        `}
}

/* Edge */
@supports (
  -ms-ime-align: auto) {
  & {
    bottom: -18px;
  }
}

${isMobile(css`
  margin-top: ${({ theme }) => theme.spacing.base};
  padding: ${({ theme }) => theme.spacing.base};
  ${containerWithBorderTopMixin}
`)}
`;

export const AccountDropdownSectionTitle = styled.span`
  color: ${({ theme }) => theme.palette.black};
  line-height: 19px;
  display: block;
  font-weight: bold;
  font-family: 'AmazonEmber-Bold', 'Amazon Ember', Arial, Helvetica, sans-serif !important;
  font-size: 11px;
  line-height: 1.465;
  margin-bottom: 6px;
  color: ${({ theme }) => theme.palette.black};
  padding: 0 18px;
`;

export const AccountDropdownSectionDivider = styled(Divider)`
  margin: 0;
`;

export const OrganizationLinkItem = styled.li`
  list-style: none;
  margin-bottom: 1px;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

interface OrganizationLinkProps {
  isCurrentlySelected: boolean;
  className?: any;
}

export const OrganizationLink = styled.a<OrganizationLinkProps>`
  &&& {
    font-size: 13px;
    color: ${({ theme }) => theme.palette.black};
    line-height: 19px;
    padding: 6px 18px;
    display: block;
    text-decoration: none;
    cursor: auto;
    user-select: text;

    &:hover {
      color: ${({ theme }) => theme.palette.black};
      background-color: ${concrete};
      text-decoration: none;
      cursor: pointer;
    }

    ${(props) =>
      props.isCurrentlySelected
        ? `
      font-weight: bold;
      font-family: "AmazonEmber-Bold", "Amazon Ember", Arial, Helvetica, sans-serif !important;
      background-color: ${concrete};
    `
        : ''};
  }
`;

export const ManageAccountLinkWrapper = styled.div`
  display: block;
  line-height: initial;

  &:not(:first-child) {
    margin-top: ${({ theme }) => theme.spacing.small};
  }
`;

export const ManageAccountLink = styled(Link)`
  display: inline;
  line-height: 19px;
`;

interface AccountsListWrapperProps {
  hasManagerAccounts: boolean;
}

/*
 * The account list wrapper's max height is limited to 398px,
 * if no manager accounts are present. The number is not determined
 * by a specific element.
 */
export const AccountsListWrapper = styled.div<AccountsListWrapperProps>`
  flex-grow: 1;
  height: initial;
  padding-bottom: 14px;

  ${(props) =>
    !props.hasManagerAccounts
      ? `
      max-height: 398px;
      overflow-y: auto;
    `
      : ''};
`;

export const ManagerAccountsListWrapper = styled.div`
  padding-bottom: 14px;
`;

export const AccountDropdownList = styled.ul`
  margin: 0;
  padding: 0;
  text-align: left;

  &:empty ${AccountsListWrapper} {
    display: none;
  }
`;

interface AccountListItemProps {
  key?: string;
  className?: any;
}

export const AccountListItem = styled.li<AccountListItemProps>`
  list-style: none;
  font-size: 13px;
  color: ${({ theme }) => theme.palette.black};
  line-height: 25px;
  padding: 0px 18px;
  cursor: pointer;

  &.${currentSelectionClassName} {
    background-color: ${concrete};
    font-weight: bold;
    font-family: 'AmazonEmber-Bold', 'Amazon Ember', Arial, Helvetica,
      sans-serif !important;
  }

  &:first-child {
    margin-top: 10px;
  }

  &:hover {
    background-color: ${mercury};
  }

  &:first-child {
    margin-top: 0;
  }

  &:last-of-type {
    margin-bottom: 13px;
  }
`;

export const AdvertisingAccount = styled.a`
  &&& {
    font-size: 13px;
    line-height: 1.465;
    margin: 0 0 2px 0;
    color: ${({ theme }) => theme.palette.black};
    text-decoration: none;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
  }
`;

interface AdvertisingAccountDataProps {
  isCurrentlySelected?: boolean;
}

export const AdvertisingAccountData = styled.span<AdvertisingAccountDataProps>`
  display: block;
  font-weight: normal;

  ${(props) =>
    props.isCurrentlySelected
      ? `
      font-weight: bold;
      font-family: "AmazonEmber-Bold", "Amazon Ember", Arial, Helvetica, sans-serif !important;
    }
  `
      : ''};
`;

export const AdvertisingAccountAccountName = styled(AdvertisingAccountData)`
  word-break: break-word;
  font-size: 13px;
  line-height: 15px;
  margin: 11px 0 0 0;

  /* Edge */
  @supports (-ms-ime-align: auto) {
    & {
      word-break: break-all;
    }
  }
`;

export const AdvertisingAccountMarketplaceName = styled(AdvertisingAccountData)`
  font-size: 11px;
  line-height: 1.465;
  color: ${({ theme }) => theme.palette.boulder};
  margin: 0 0 5.5px 0;
`;

export const AdvertisingAccountEntityId = styled(AdvertisingAccountData)`
  font-size: 11px;
  line-height: 1.465;
  color: ${({ theme }) => theme.palette.boulder};
  margin: 11px 0 0 0;
  padding-left: 10px;
`;

export const AccountListScrollableContainer = styled.div<AccountsListWrapperProps>`
  flex: 1;
  overflow-y: auto;
`;

export const AccountTypeLabel = styled(Text)`
  font-weight: 700;
`;

export const IA2AccountDropdownList = styled.ul`
  margin: 0;
  padding: 0;
  text-align: left;

  ${isMobile(css`
    display: grid;
    gap: ${({ theme }) => theme.spacing.medium};
  `)}
  &:empty ${AccountsListWrapper} {
    display: none;
  }
`;

export const IA2AccountDropdownSearchInput = styled(SearchInput)`
  input[type='search']:focus {
    background-color: ${white};
  }
`;

export const CentricAccountDropdownSearchInput = styled(SearchInput)`
  padding-inline-end: 10px;
`;

export const StyledIntroWidgetAnchor = styled(IntroWidgetAnchor)`
  display: block;
  padding: ${({ theme }) => theme.spacing.base}
    ${({ theme }) => theme.spacing.base} 0;

  :empty {
    padding: 0;
  }

  ${isMobile(css`
    padding-bottom: ${({ theme }) => theme.spacing.base};

    :empty {
      border-top: none;
    }

    ${containerWithBorderTopMixin}
  `)}
`;

export const CentricPaginationButton = styled(TextButton)`
  text-align: center;
  display: block;
  margin: auto;
  padding: ${({ theme }) => theme.spacing.base} 0;

  ${isMobile(css`
    padding-top: ${({ theme }) => theme.spacing.base};
  `)}
`;
